import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestViewApprovalCount,
  requestViewApprovalMembers,
  requestViewApprovals,
  requestUpdatePostStatus,
  requestUpdateCommentStatus,
  requestUpdateAllPostStatus,
  requestUpdateAllCommentStatus,
  requestViewApprovalsForUser,
  requestEvaluateSupportConversation
} from './approval.actions';
import {
  getViewApprovalCountRequest,
  getViewApprovalMembersRequest,
  getViewApprovalsRequest,
  getUpdatePostStatusRequest,
  getUpdateCommentStatusRequest,
  getUpdateAllPostStatusRequest,
  getUpdateAllCommentStatusRequest,
  getViewApprovalsForUserRequest,
  getEvaluateSupportConversationRequest
} from './approval.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setApprovalCount, setApprovalMembers, setPosts } from '../../slices/programme/tribe/tribe.slice';

import { ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performViewApprovalCountRequest({ payload }) {
  const { programmeExternalReference, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewApprovalCountRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setApprovalCount(data.pendingApprovalCount));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewApprovalCountRequest() {
  yield takeLatest(requestViewApprovalCount.type, performViewApprovalCountRequest);
}

export function* performViewApprovalMembersRequest({ payload }) {
  const { programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving members'));

    const [endpoint, options] = getViewApprovalMembersRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setApprovalMembers(data.memberApprovalDetails));
    yield put(removeSpinner('Retrieving members'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving members'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewApprovalMembersRequest() {
  yield takeLatest(requestViewApprovalMembers.type, performViewApprovalMembersRequest);
}

export function* performViewApprovalsRequest({ payload }) {
  const { programmeExternalReference, type, status, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving approvals'));

    const [endpoint, options] = getViewApprovalsRequest(programmeExternalReference, type, status);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setPosts(data.posts));
    yield put(removeSpinner('Retrieving approvals'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving approvals'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewApprovalsRequest() {
  yield takeLatest(requestViewApprovals.type, performViewApprovalsRequest);
}

export function* performUpdatePostStatusRequest({ payload }) {
  const { programmeExternalReference, postExternalReference, status, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating post status'));

    const [endpoint, options] = getUpdatePostStatusRequest(programmeExternalReference, postExternalReference, status);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating post status'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating post status'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdatePostStatusRequest() {
  yield takeLatest(requestUpdatePostStatus.type, performUpdatePostStatusRequest);
}

export function* performUpdateCommentStatusRequest({ payload }) {
  const { programmeExternalReference, commentExternalReference, status, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating comment status'));

    const [endpoint, options] = getUpdateCommentStatusRequest(
      programmeExternalReference,
      commentExternalReference,
      status
    );

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating comment status'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating comment status'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateCommentStatusRequest() {
  yield takeLatest(requestUpdateCommentStatus.type, performUpdateCommentStatusRequest);
}

export function* performUpdateAllPostStatusRequest({ payload }) {
  const { programmeExternalReference, systemUserExternalReference, status, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating all posts'));

    const [endpoint, options] = getUpdateAllPostStatusRequest(
      programmeExternalReference,
      systemUserExternalReference,
      status
    );

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating all posts'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating all posts'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateAllPostStatusRequest() {
  yield takeLatest(requestUpdateAllPostStatus.type, performUpdateAllPostStatusRequest);
}

export function* performUpdateAllCommentStatusRequest({ payload }) {
  const { programmeExternalReference, systemUserExternalReference, status, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating all comments'));

    const [endpoint, options] = getUpdateAllCommentStatusRequest(
      programmeExternalReference,
      systemUserExternalReference,
      status
    );

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating all comments'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating all comments'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateAllCommentStatusRequest() {
  yield takeLatest(requestUpdateAllCommentStatus.type, performUpdateAllCommentStatusRequest);
}

export function* performViewApprovalsForUserRequest({ payload }) {
  const { programmeExternalReference, systemUserExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving approvals'));

    const [endpoint, options] = getViewApprovalsForUserRequest(programmeExternalReference, systemUserExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setPosts(data.posts));
    yield put(removeSpinner('Retrieving approvals'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving approvals'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewApprovalsForUserRequest() {
  yield takeLatest(requestViewApprovalsForUser.type, performViewApprovalsForUserRequest);
}

export function* performEvaluateSupportConversationRequest({ payload }) {
  const { programmeExternalReference, systemUserExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Evaluating support conversation'));

    const [endpoint, options] = getEvaluateSupportConversationRequest(
      programmeExternalReference,
      systemUserExternalReference
    );

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Evaluating support conversation'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Evaluating support conversation'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForEvaluateSupportConversationRequest() {
  yield takeLatest(requestEvaluateSupportConversation.type, performEvaluateSupportConversationRequest);
}

export default function* approvalSaga() {
  yield all([
    watchForViewApprovalCountRequest(),
    watchForViewApprovalMembersRequest(),
    watchForViewApprovalsRequest(),
    watchForUpdatePostStatusRequest(),
    watchForUpdateCommentStatusRequest(),
    watchForUpdateAllPostStatusRequest(),
    watchForUpdateAllCommentStatusRequest(),
    watchForViewApprovalsForUserRequest(),
    watchForEvaluateSupportConversationRequest()
  ]);
}
